import { AfterViewInit, Component, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import { provideDayjsDateAdapter } from '@app/shared/extra/dayjs-date-adapter';
import dayjs, { ConfigType, Dayjs } from 'dayjs';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

@Component({
  selector: 'date-field-builder',
  templateUrl: './date-field-builder.component.html',
  styleUrls: ['./date-field-builder.component.scss'],
  providers: [provideDayjsDateAdapter()],
  standalone: false
})
export class DateFieldBuilderComponent extends AbstractFieldBuilder<FormControl<ConfigType>> implements OnInit, AfterViewInit {

  protected initialFormValue: ConfigType;

  constructor() {
    super();
    // By default, the form value is a Dayjs obtained from the original string value
    this.initialFormValue = this.fieldInitValue ? dayjs(this.fieldInitValue) : null;
  }

  /**
   * Initialize form's value and FormStateService, compute form validators.
   */
  public ngOnInit(): void {
    this.form = new FormGroup({
      field: new FormControl(this.initialFormValue, this.computeValidators())
    });
    // Initialise the field in the FormStateService
    this.setFieldValue(this.formatDate(this.initialFormValue));
    this.setFieldInitialValue(this.formatDate(this.initialFormValue));
    this.getNextState();
  }

  /**
   * Setup validator hooks and listen for user input on the form field. When a new date value is received, propagate
   * to the FormStateService, including validation subject. Prior to propagating, the value from the form field is
   * transformed by {@see formatDate}.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();

    this.form.controls.field.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      map(value => this.formatDate(value)),
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe(formattedDate => {
        this.setFieldValue(formattedDate);
        if (this.formStateService.getPath(['AFTER_OTHER_DATE', this.fieldConfig.fieldCode])) {
          this.formStateService.getPath(['AFTER_OTHER_DATE', this.fieldConfig.fieldCode])
            .next(formattedDate);
        }
      });
  }

  /**
   * Cancel edition of this field, reset form value to its initial value.
   */
  public cancel(): void {
    this.form.controls.field.setValue(this.initialFormValue);
    super.cancel();
  }

  /**
   * Generate a ValidatorFn with the field's config's validators, except 'AFTER_OTHER_DATE' since it is replaced by an
   * actual afterDate validator when calling setupHooks().
   * @return Composed validator.
   * @protected
   */
  protected computeValidators(): ValidatorFn {
    return Validators.compose(this.fieldConfig.field?.validators
      .map(validator => this.validationService.getValidator(validator, this.entity))
    );
  }

  /**
   * Setup field hooks. If the field has a validator whose definition depends on another field's value, listen for
   * changes to this field's value and update the validator accordingly.
   * @protected
   */
  protected setupHooks(): void {
    super.setupHooks();

    // Retrieve AFTER_OTHER_DATE validators to be converted to afterDate
    const afterDateFieldValidators = this.fieldConfig.field?.validators
      .filter(validator => validator.type === ValidatorType.AFTER_OTHER_DATE);

    // Setup hook to afterDate validators if they exist on this field
    afterDateFieldValidators.forEach(afterDateFieldValidator => {
      // Create a hook on the value change
      if (!this.formStateService.getPath([afterDateFieldValidator.type, afterDateFieldValidator.definitionPath.last()])) {
        this.formStateService.setPath([
          afterDateFieldValidator.type,
          afterDateFieldValidator.definitionPath.last()
        ], new Subject<Dayjs>());
      }

      // Update afterDate validation when reference field is updated upstream
      this.formStateService.getPath([afterDateFieldValidator.type, afterDateFieldValidator.definitionPath.last()])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((date: string) => {
          // Keep everything but current referenced afterDate and afterDateField validators
          this.fieldConfig.field.validators = this.fieldConfig.field.validators
            .filter(validator =>
              validator.ref !== afterDateFieldValidator.definitionPath.last()
            );

          // Push a new afterDate validator with the updated value and its reference
          this.fieldConfig.field?.validators.push(
            this.validationService.convertFieldDependantValidator(afterDateFieldValidator, date)
          );

          // Skip re-adding the fieldtype-specific validators
          this.form.controls.field.setValidators(this.computeValidators());
          this.form.controls.field.updateValueAndValidity({onlySelf: false, emitEvent: false});
          this.form.controls.field.markAsTouched({onlySelf: false});
        });
    });
  }

  /**
   * Format a date value from the form field as a string represented date. Return an empty string if the value is
   * null or not a valid date. By default, input values are expected to be Dayjs objects.
   * @param value Form value.
   * @return String representation of the date or empty string.
   * @protected
   */
  protected formatDate(value?: ConfigType): string {
    return dayjs.isDayjs(value) && value.isValid() ? value.format(this.appConfig.DATE_FORMAT) : '';
  }

  /**
   * Format a date value based on the user's locale. Return an empty string if the value is null or not a valid date.
   * @param value Form value. Should be a string representing an ISO date.
   * @return Localized string representation of the date or empty string.
   * @protected
   */
  protected formatLocalizedDate(value?: ConfigType): string {
    return value && dayjs(value).isValid() ? dayjs(value).format(this.appConfig.DISPLAY_DATE_FORMAT) : '';
  }

  /**
   * Get the field value to display in read mode.
   * @return Field's value
   */
  public getFieldValue(): string {
    return this.formatLocalizedDate(super.getFieldValue());
  }
}
